import React, { useEffect, useRef, useState } from 'react';
import {
	Libraries,
	StandaloneSearchBox,
	useJsApiLoader,
} from '@react-google-maps/api';
import { Quotation } from '../../../types/api/Quotation';
import { useApi } from '../../../hooks/useApi';
import { ActionButtons } from '../../../globalComponents/modal/ActionButtons';
import { Modal } from '../../../globalComponents/modal/Modal';
import { QuoteState } from '../../../types/stateProviders/QuoteStateProvider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { toastSuccess, toastError } from '../../../Toaster/Toast.tsx';

const mapsLibraries: Libraries = ['places'];

export const EditQuotationModal: React.FC<{
	open: boolean;
	onClose: () => void;
	quotation: Quotation;
	setQuotation: React.Dispatch<Partial<QuoteState>>;
}> = ({ open, onClose, quotation, setQuotation }) => {
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY || '',
		libraries: mapsLibraries,
	});

	const [quote, setQuote] = useState<Quotation>(quotation);
	const api = useApi();
	const [error, setError] = useState(false);

	const addressRef = useRef<google.maps.places.SearchBox>();

	useEffect(() => {
		if (open) {
			setQuote(quotation);
		}
	}, [open, quotation]);

	const handleOnPlacesChanged = () => {
		if (addressRef.current) {
			const places = addressRef.current.getPlaces();
			const place = places?.[0];

			if (place) {
				const formattedAddress = place.formatted_address || '';
				const hasStreetNumber = place.address_components?.some((component) =>
					component.types.includes('street_number')
				);
				const hasRoute = place.address_components?.some((component) =>
					component.types.includes('route')
				);

				if (!hasStreetNumber || !hasRoute) {
					toastError({
						title: 'Incomplete Address',
						description:
							'Please enter a complete street address (e.g., 123 Main St, City, State)',
					});
					setQuote((prev) => ({
						...prev,
						address: '',
						zipCode: '',
					}));
					return;
				}

				const zipComponent = place.address_components?.find((component) =>
					component.types.includes('postal_code')
				);
				const zipCode = zipComponent?.long_name || '';

				if (!zipCode) {
					toastError({
						title: 'Invalid Address',
						description: 'Please select an address with a valid zip code',
					});
					setQuote((prev) => ({
						...prev,
						address: formattedAddress,
						zipCode: '',
					}));
				} else {
					setQuote((prev) => ({
						...prev,
						address: formattedAddress,
						zipCode: zipCode,
					}));
				}
			}
		}
	};

	const modalTitle = (
		<Typography variant="h2" fontWeight="bold">
			Edit Quotation Details: {quotation.projectName}
		</Typography>
	);

	const modalBody = (
		<Stack spacing={2} sx={{ mb: 2 }}>
			<Stack>
				<FormLabel component="legend" sx={{ mb: 1 }} required>
					Project Name
				</FormLabel>
				<TextField
					fullWidth
					name="projectName"
					type="text"
					placeholder="Example: Johnson Residence Outdoor Deck"
					value={quote.projectName}
					onChange={(e) => setQuote({ ...quote, projectName: e.target.value })}
				/>
			</Stack>
			<Stack>
				<FormLabel component="legend" sx={{ mb: 1 }} required>
					Customer Name
				</FormLabel>
				<TextField
					fullWidth
					name="customerName"
					type="text"
					placeholder="Example: Johnson Freeman"
					value={quote.customerName}
					onChange={(e) => setQuote({ ...quote, customerName: e.target.value })}
				/>
			</Stack>
			<Stack>
				<FormLabel component="legend" sx={{ mb: 1 }} required>
					Address
				</FormLabel>

				{isLoaded && (
					<StandaloneSearchBox
						onLoad={(ref) => (addressRef.current = ref)}
						onPlacesChanged={handleOnPlacesChanged}
					>
						<TextField
							fullWidth
							name="address"
							type="text"
							placeholder="Example: 1234 Oak Lane, Beverly Hills, CA"
							value={quote.address}
							onChange={(e) => setQuote({ ...quote, address: e.target.value })}
						/>
					</StandaloneSearchBox>
				)}
			</Stack>
		</Stack>
	);

	const modalFooter = (
		<ActionButtons
			confirmButtonText="Save"
			confirmButtonStyle="success"
			confirmCallback={async () => {
				if (quote._id) {
					await api.quotations.updateQuotation(quote._id, {
						projectName: quote.projectName,
						customerName: quote.customerName,
						zipCode: quote.zipCode,
						address: quote.address,
					});
					toastSuccess({
						title: 'Quotation Updated',
						description: `You have successfully updated the quotation: ${quote.projectName}`,
					});
					setQuotation({
						currentQuote: quote,
						projectName: quote.projectName,
						customerName: quote.customerName,
						zipCode: quote.zipCode,
						address: quote.address,
					});
					setError(false);
					onClose();
				} else {
					console.error('Quotation ID is undefined');
				}
			}}
			confirmDisabled={
				error ||
				!quote.projectName ||
				!quote.customerName ||
				!quote.zipCode ||
				!quote.address
			}
			cancelCallback={onClose}
		/>
	);

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle={modalTitle}
			modalBody={modalBody}
			modalFooter={modalFooter}
			minWidth="500px"
		/>
	);
};
